// theme.js — FMX Data Migration Tool design tokens
// Drop-in replacement for src/theme.js, aligned to the FMX product
// design system ("native FMX" restyle, approved baseline).
//
// New code should use the canonical T.* names. The legacy `C` export
// keeps the old keys compiling during migration — each maps to the
// nearest new token; retire them as components are restyled.

export const T = {
  // ---- Core ----
  navy:        '#012986', // left nav, dark emphasis
  brand:       '#0d7f9b', // primary buttons, links, active tabs
  brandHover:  '#0a5f76',
  brandActive: '#074555',
  cyan:        '#00b8dd', // nav active border, dependency arrows, gradient end

  // ---- Blue tints ----
  blue50:  '#f0f5f7', // selected wash, category bands, light chips
  blue100: '#dbebf4', // progress track
  blue200: '#a8ddeb', // accent card borders

  // ---- Ink ----
  ink:      '#38383d',
  inkMuted: '#616161',
  inkSoft:  '#767676',

  // ---- Surfaces & borders ----
  bg:           '#f9f9fa',
  bgMuted:      '#f0f5f7',
  white:        '#ffffff',
  border:       '#dddddd', // default + spreadsheet grid lines
  borderSubtle: '#ededed', // hairlines, card borders
  borderStrong: '#8d9496', // inputs

  // ---- Semantic ----
  red500:     '#cb3325',
  redDarker:  '#8a1e14',
  orange500:  '#e0711f',
  orangeText: '#b4540f', // orange text on light washes (contrast-safe)
  green500:   '#77ab13',
  greenDark:  '#009f0b', // success text on light tints
  greenText:  '#3f7d10',

  // Brand-guide orange — RESERVED for "current phase / in progress"
  // markers (active row accent, in-progress tokens, phase dot). Not buttons.
  accentOrange: '#CE4A12',

  // ---- Badge fills (text is always T.ink) ----
  badge: {
    red:       '#fe8e91',
    orange:    '#fec9b8',
    pink:      '#fadbd1',
    blueLight: '#c9e2e8',
    blueDark:  '#8bc5ce',
    green:     '#cce3a6',
    gray:      '#dddddd',
  },

  // ---- Tint washes (alerts, error/warning cells) ----
  tint: {
    info:    '#e7f4f7',
    success: '#eaf2da',
    warning: '#faede7',
    error:   '#f5ebea',
  },

  // ---- Type ----
  fontSans: '"Figtree", -apple-system, "Segoe UI", system-ui, sans-serif',
  fontData: '"Inter", ui-monospace, Menlo, Consolas, monospace', // numerals, counts, costs

  // ---- Shape ----
  radiusCard: 8,   // cards, panels (tables: 5)
  radiusTable: 5,
  radiusBtn: 4,    // buttons, inputs
  radiusPill: 100, // badges, chips, avatars

  // ---- Elevation ----
  shadowSm:   '1px 3px 5px 0 rgba(97,97,97,0.15)',
  shadowMd:   '2px 5px 5px 0 rgba(97,97,97,0.15)',
  shadowLg:   '0 8px 20px 0 rgba(97,97,97,0.20)',
  focusRing:  '0 0 0 3px rgba(13,127,155,0.30)',

  // ---- Misc ----
  gradientProgress: 'linear-gradient(90deg, #0d7f9b, #00b8dd)',
  codeBg: '#0f1b3d',
  codeFg: '#cfe0f5',
};

// ---- Legacy aliases (migration shim for old C.* usages) ----
// Note deliberate role changes:
//   C.orange was the primary-button color → primary buttons are now T.brand (teal).
//   C.navy (#041662) → nav/dark emphasis is now T.navy (#012986).
export const C = {
  orange:     T.brand,        // primary action color is now teal
  orangeHov:  T.brandHover,
  navy:       T.navy,
  blue:       T.cyan,
  white:      T.white,
  bgPage:     T.bg,
  border:     T.border,
  textDark:   T.ink,
  textMid:    T.inkMuted,
  textLight:  T.inkSoft,
  navyTint:   T.blue50,
  errBg:      T.tint.error,
  errText:    T.red500,
  errBorder:  T.badge.red,
  warnBg:     T.tint.warning,
  warnText:   T.orange500,
  warnBorder: T.badge.orange,
  okBg:       T.tint.success,
  okText:     T.greenText,
  okBorder:   T.badge.green,
  blueBg:     T.tint.info,
  blueBorder: T.badge.blueLight,
};

export default T;
